import { get } from 'lodash'
import { renderComponent } from 'utils/dynamicRenderingService'
import { toFiltersUrl } from './serializers'
import { formatUIDate, getDefaultItemValue } from 'utils/helpers'
import { FilterParams, FilterSchema, Filter } from './TableDefinitions'

const getInitialFilterParam = (searchParams: FilterParams, filter: FilterSchema): string => {
  const { variable, defaultValue } = filter

  if (!variable && !defaultValue) return filter.type == 'date' ? null : ''

  return searchParams[variable] ?? getDefaultItemValue(defaultValue)
}

export const convertFilterSchemaToFilterConfig = (
  filterSchema: FilterSchema,
  searchParams: FilterParams
) => {
  const { name, defaultValue, label, required, hidden, ...componentProps } = filterSchema

  return {
    name,
    label,
    type: componentProps.type,
    required,
    hidden,
    value: getInitialFilterParam(searchParams, filterSchema),
    filter: renderComponent({ ...componentProps, label })
  }
}

export const getSearchFieldsGetUrl = (searchFields: string[], searchText: string) => {
  if (!searchText) return ''

  const searchBy = searchFields.reduce(
    (acc, field) => (acc = acc ? `${acc}${encodeURIComponent('|')}${field}` : field),
    ''
  )

  return `&searchBy=${searchBy}&searchValue=${encodeURIComponent(searchText)}`
}

const getFilterUrlValues = (filters: Filter[]) => {
  return filters.reduce(
    (acc, filter) => {
      const value = Array.isArray(filter.value)
        ? `[${filter.value.map(val => (typeof val === 'string' ? val : val.value)).join(';')}]`
        : filter.value && typeof filter.value === 'object'
        ? filter.value.value
        : filter.value

      const encodedValue = encodeURIComponent(
        value === undefined || value === null ? '' : (value as string)
      )

      acc = [
        acc[0] ? `${acc[0]}${encodeURIComponent('|')}${filter.name}` : filter.name,
        acc[1] ? `${acc[1]}${encodeURIComponent('|')}${encodedValue}` : encodedValue
      ]
      return acc
    },
    ['', '']
  )
}

export const getFiltersGetUrl = (filters: Filter[]) => {
  if (!filters.length) return ''

  const [filterBy, filterValue] = getFilterUrlValues(filters)

  return `&filterBy=${filterBy}&filterValue=${filterValue}`
}

export const getSearchGetUrl = (searchFields: string[], searchText: string) => {
  if (!searchText) return ''
  const searchBy = encodeURIComponent(searchFields.join('|'))
  const searchValue = encodeURIComponent(searchText)

  return `&searchBy=${searchBy}&searchValue=${searchValue}`
}

export const getCustomFiltersGetUrl = (filters: Filter[]) => {
  if (!filters.length) return ''

  const [filterBy, filterValue] = getFilterUrlValues(filters)

  return `&${filterBy}=${filterValue}`
}

export const getAllFilters = (filters: Filter[]) => {
  return filters.filter(filter => {
    // Check if value is undefined or null
    if (filter.value === undefined || filter.value === null || filter.value === '') {
      return false
    }

    // Check if value is an empty string or a AutoCompleteValue with an empty 'value'
    if (typeof filter.value === 'object' && 'value' in filter.value && filter.value.value === '') {
      return false
    }

    // If value is an array, ensure it's non-empty and does not contain empty strings or AutoCompleteValues
    if (Array.isArray(filter.value) && filter.value.length === 0) {
      return false // Exclude empty arrays
    }

    return true
  })
}

export const getTableQueryUrl = params =>
  Object.keys(params).reduce(
    (acc, key) =>
      (acc =
        params[key] || params[key] === 0
          ? acc
            ? `${acc}&${key}=${params[key]}`
            : `${key}=${params[key]}`
          : acc),
    ''
  )

export const renderTableFooter = (footerElements, summary) =>
  footerElements.map(element => {
    return renderComponent({ ...element, data: summary, key: element.id })
  })